// File: /Users/pramod/Desktop/school-erp/frontend/src/ControllerofExammination/PromotionPolicies.js

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';
import {
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  FileText,
  AlertCircle,
  Sliders,
  Layers,
  GraduationCap,
  Sparkles,
  Info
} from 'lucide-react';

const PromotionPolicies = () => {
  const location = useLocation();
  const { tenant } = location.state || {};
  
  const [tenantId] = useState(tenant?.id || tenant?._id || localStorage.getItem('tenantId'));
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Notification and confirmation modal states
  const [notification, setNotification] = useState({ show: false, type: 'info', title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  // Main UI Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  
  // Program Type and Program selection
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramTypeId, setSelectedProgramTypeId] = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [loadingProgramTypes, setLoadingProgramTypes] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityError, setPriorityError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    policyName: '',
    description: '',
    promotionRules: []
  });
  
  const [editingRule, setEditingRule] = useState(null);
  const [showRuleModal, setShowRuleModal] = useState(false);
  
  // Rule setup
  const [newRule, setNewRule] = useState({
    ruleName: '',
    priority: 1,
    conditions: {
      overallPercentage: { min: '' },
      failedSubjectsCount: { max: '' }
    },
    action: 'PROMOTE'
  });

  const actionOptions = [
    { value: 'PROMOTE', label: 'Pass & Promote', description: 'Student moves to the next year or semester with a clean slate.', color: 'green' },
    { value: 'PROMOTE_WITH_BACKLOG', label: 'Promote with Backlog / Carry Forward', description: 'Student moves to the next year but must clear their failed papers later.', color: 'blue' },
    { value: 'YEAR_BACK', label: 'Detain / Repeat Year', description: 'Student is held back in the current academic year.', color: 'red' }
  ];

  const triggerAlert = (title, message, type = 'info') => {
    setNotification({ show: true, type, title, message });
  };

  const triggerConfirm = (title, message, callback) => {
    setConfirmModal({ show: true, title, message, onConfirm: () => { callback(); setConfirmModal({ show: false }); } });
  };

  const generateUniqueId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  // Fetch Program Types
  useEffect(() => {
    if (!tenantId) return;
    setLoadingProgramTypes(true);
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => setProgramTypes(res.data.programTypes || []))
      .catch(err => console.error('Failed to load program types:', err))
      .finally(() => setLoadingProgramTypes(false));
  }, [tenantId]);

  // Fetch Programs
  useEffect(() => {
    if (!tenantId || !selectedProgramTypeId) {
      setPrograms([]);
      return;
    }
    setLoadingPrograms(true);
    api.get('/api/programs', { params: { tenantId, programTypeId: selectedProgramTypeId, status: 'active' } })
      .then(res => setPrograms(res.data.programs || []))
      .catch(err => console.error('Failed to load programs:', err))
      .finally(() => setLoadingPrograms(false));
  }, [tenantId, selectedProgramTypeId]);

  // Fetch policies
  const fetchPolicies = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const params = { tenantId };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (selectedProgramId) params.programId = selectedProgramId;
      else if (selectedProgramTypeId) params.programTypeId = selectedProgramTypeId;
      
      const response = await api.get('/api/coe/promotion-policies/', { params });
      setPolicies(response.data.data || []);
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter, selectedProgramId, selectedProgramTypeId]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const checkDuplicatePriorities = (rules) => {
    const priorities = rules.map(r => r.priority);
    const unique = [...new Set(priorities)];
    if (priorities.length !== unique.length) {
      setPriorityError('Duplicate Order Value! Each rule must have a unique execution order.');
      return false;
    }
    setPriorityError('');
    return true;
  };

  const formatConditionText = (conditions) => {
    if (!conditions) return 'No criteria specified (Applies to all students)';
    
    const parts = [];
    if (conditions.overallPercentage?.min) {
      parts.push(`Overall score is ${conditions.overallPercentage.min}% or higher`);
    }
    if (conditions.failedSubjectsCount?.max !== undefined && conditions.failedSubjectsCount.max !== null && conditions.failedSubjectsCount.max !== '') {
      parts.push(`Fails at most ${conditions.failedSubjectsCount.max} subjects`);
    }
    return parts.length ? parts.join(' AND ') : 'No criteria specified (Applies to all students)';
  };

  const handleCreatePolicy = async () => {
    if (!formData.policyName) {
      triggerAlert('Required Field', 'Please provide a clear title for this promotion policy.', 'warning');
      return;
    }
    if (formData.promotionRules.length === 0) {
      triggerAlert('Missing Rules', 'A policy must contain at least one promotion rule.', 'warning');
      return;
    }
    if (!checkDuplicatePriorities(formData.promotionRules)) return;
    
    try {
      let scope = { type: 'tenant' };
      if (selectedProgramId) {
        scope = { type: 'program', programId: selectedProgramId };
      } else if (selectedProgramTypeId) {
        scope = { type: 'program_type', programTypeId: selectedProgramTypeId };
      }
      
      const rulesToSend = formData.promotionRules.map(({ _id, ...rule }) => rule);
      const payload = {
        tenantId,
        policyName: formData.policyName,
        description: formData.description,
        scope,
        promotionRules: rulesToSend
      };
      
      const response = await api.post('/api/coe/promotion-policies/', payload);
      if (response.data.success) {
        setShowCreateModal(false);
        resetForm();
        fetchPolicies();
        triggerAlert('Success', 'Promotion policy created successfully!', 'success');
      }
    } catch (error) {
      triggerAlert('Submission Error', error.response?.data?.message || error.message, 'error');
    }
  };

  const handleUpdatePolicy = async () => {
    if (!checkDuplicatePriorities(formData.promotionRules)) return;
    
    try {
      const rulesToSend = formData.promotionRules.map(({ _id, ...rule }) => rule);
      const response = await api.put(`/api/coe/promotion-policies/${selectedPolicy._id}?tenantId=${tenantId}`, {
        policyName: formData.policyName,
        description: formData.description,
        promotionRules: rulesToSend
      });
      
      if (response.data.success) {
        setShowEditModal(false);
        resetForm();
        fetchPolicies();
        triggerAlert('Updated', 'Policy layout successfully modified.', 'success');
      }
    } catch (error) {
      triggerAlert('Update Error', error.response?.data?.message || error.message, 'error');
    }
  };

  const handleActivatePolicy = (policyId) => {
    triggerConfirm(
      'Activate Promotion Policy?',
      'Activating this policy will make it immediately active for processing student promotion eligibility.',
      async () => {
        try {
          await api.post(`/api/coe/promotion-policies/${policyId}/activate?tenantId=${tenantId}`);
          fetchPolicies();
        } catch (error) {
          triggerAlert('Operation Failed', 'Could not activate the selected policy.', 'error');
        }
      }
    );
  };

  const handleArchivePolicy = (policyId) => {
    triggerConfirm(
      'Archive Promotion Policy?',
      'Are you sure you want to archive this policy? This will hide it from the active list, but historical data will remain saved.',
      async () => {
        try {
          await api.delete(`/api/coe/promotion-policies/${policyId}?tenantId=${tenantId}&reason=User_Archived_Request`);
          fetchPolicies();
        } catch (error) {
          triggerAlert('Operation Failed', 'Could not archive the selected policy.', 'error');
        }
      }
    );
  };

  const handleEditPolicy = (policy) => {
    setSelectedPolicy(policy);
    const rulesWithUniqueIds = (policy.promotionRules || []).map(rule => ({
      ...rule,
      _id: rule._id || generateUniqueId(),
      conditions: rule.conditions || {}
    }));
    setFormData({
      policyName: policy.policyName,
      description: policy.description || '',
      promotionRules: rulesWithUniqueIds
    });
    setShowEditModal(true);
  };

  const handlePreviewPolicy = (policy) => {
    setSelectedPolicy(policy);
    setShowPreviewModal(true);
  };

  const saveRule = () => {
    if (!newRule.ruleName) {
      triggerAlert('Rule Name Missing', 'Please enter a name for this specific rule step.', 'warning');
      return;
    }
    
    const existingPriorities = formData.promotionRules.filter(r => r._id !== editingRule?._id).map(r => r.priority);
    if (existingPriorities.includes(parseInt(newRule.priority))) {
      triggerAlert('Order Conflict', `Step order #${newRule.priority} is already being used. Please choose a different number.`, 'warning');
      return;
    }
    
    const cleanedConditions = {};
    if (newRule.conditions.overallPercentage.min && newRule.conditions.overallPercentage.min !== '') {
      cleanedConditions.overallPercentage = { min: parseFloat(newRule.conditions.overallPercentage.min) };
    }
    if (newRule.conditions.failedSubjectsCount.max && newRule.conditions.failedSubjectsCount.max !== '') {
      cleanedConditions.failedSubjectsCount = { max: parseInt(newRule.conditions.failedSubjectsCount.max) };
    }
    
    const ruleToSave = {
      _id: editingRule?._id || generateUniqueId(),
      ruleName: newRule.ruleName,
      priority: parseInt(newRule.priority),
      conditions: cleanedConditions,
      action: newRule.action
    };
    
    let updatedRules;
    if (editingRule) {
      updatedRules = formData.promotionRules.map(r => r._id === editingRule._id ? ruleToSave : r);
    } else {
      updatedRules = [...formData.promotionRules, ruleToSave];
    }
    
    setFormData({ ...formData, promotionRules: updatedRules });
    checkDuplicatePriorities(updatedRules);
    setShowRuleModal(false);
    setEditingRule(null);
    resetNewRule();
  };

  const editRule = (rule) => {
    setEditingRule(rule);
    setNewRule({
      ruleName: rule.ruleName || '',
      priority: rule.priority || 1,
      conditions: {
        overallPercentage: { min: rule.conditions?.overallPercentage?.min || '' },
        failedSubjectsCount: { max: rule.conditions?.failedSubjectsCount?.max !== undefined ? rule.conditions.failedSubjectsCount.max : '' }
      },
      action: rule.action || 'PROMOTE'
    });
    setShowRuleModal(true);
  };

  const deleteRule = (ruleId) => {
    const updatedRules = formData.promotionRules.filter(r => r._id !== ruleId);
    setFormData({ ...formData, promotionRules: updatedRules });
    checkDuplicatePriorities(updatedRules);
  };

  const resetForm = () => {
    setFormData({ policyName: '', description: '', promotionRules: [] });
    setEditingRule(null);
    resetNewRule();
    setPriorityError('');
  };

  const resetNewRule = () => {
    setNewRule({
      ruleName: '',
      priority: 1,
      conditions: { overallPercentage: { min: '' }, failedSubjectsCount: { max: '' } },
      action: 'PROMOTE'
    });
  };

  const handleProgramTypeSelection = (value) => {
    setSelectedProgramTypeId(value);
    setSelectedProgramId('');
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      draft: 'bg-amber-50 text-amber-700 border-amber-200',
      archived: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${styles[status] || styles.draft}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const getActionBadge = (action) => {
    const option = actionOptions.find(opt => opt.value === action);
    const colorMap = {
      green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      blue: 'bg-sky-50 text-sky-700 border-sky-200',
      red: 'bg-rose-50 text-rose-700 border-rose-200'
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${colorMap[option?.color || 'gray']}`}>
        {option?.label || action?.replace(/_/g, ' ')}
      </span>
    );
  };

  const getScopeLabel = (policy) => {
    if (policy.scope?.type === 'tenant') return 'Entire Institution';
    if (policy.scope?.type === 'program_type') return 'Department / Stream Level';
    if (policy.scope?.type === 'program') return 'Specific Degree Course';
    return 'General Scope';
  };

  if (loading && policies.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-2xl border border-slate-100 shadow-xl max-w-sm w-full">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-sm font-medium text-slate-700">Loading promotion rules and settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased pb-24">
      
      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[100] p-4 transition-all animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 transform transition-transform scale-100">
            <h3 className="text-lg font-bold text-slate-900 mb-2">{confirmModal.title}</h3>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">{confirmModal.message}</p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button onClick={() => setConfirmModal({ show: false })} className="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors">Cancel</button>
              <button onClick={confirmModal.onConfirm} className="w-full sm:w-auto px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg shadow-indigo-600/20 transition-all">Proceed</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Notification Modal */}
      {notification.show && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[110] p-4 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-xl ${notification.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                <Info className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">{notification.title}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">{notification.message}</p>
            <button onClick={() => setNotification({ show: false })} className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors">Dismiss</button>
          </div>
        </div>
      )}

      {/* Top Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm shadow-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs tracking-wider uppercase mb-0.5">
                <Sparkles className="w-3.5 h-3.5" /> Examination Control Panel
              </div>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">Promotion & Passing Rules</h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Set the criteria for how students pass to the next year, carry forward failed subjects, or repeat a year.</p>
            </div>
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 text-white font-bold text-sm rounded-xl hover:bg-indigo-700 shadow-md shadow-indigo-600/10 transition-all active:scale-[0.98]"
            >
              <Plus className="w-4 h-4 stroke-[3]" />
              Create New Policy
            </button>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 space-y-6">
        
        {/* Step 1: Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            <Sliders className="w-4 h-4 text-slate-500" />
            <h2 className="text-sm font-bold text-slate-900">Step 1: Choose Academic Area</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 tracking-wide uppercase">
                <Layers className="w-3 h-3 text-indigo-500" /> Program Category / Stream
              </label>
              <select
                value={selectedProgramTypeId}
                onChange={(e) => handleProgramTypeSelection(e.target.value)}
                className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                disabled={loadingProgramTypes}
              >
                <option value="">All Streams</option>
                {programTypes.map(pt => (
                  <option key={pt._id} value={pt._id}>{pt.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 tracking-wide uppercase">
                <GraduationCap className="w-3.5 h-3.5 text-indigo-500" /> Specific Course / Degree
              </label>
              <select
                value={selectedProgramId}
                onChange={(e) => setSelectedProgramId(e.target.value)}
                className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none disabled:opacity-50"
                disabled={!selectedProgramTypeId || loadingPrograms}
              >
                <option value="">All Courses under this Stream</option>
                {programs.map(p => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2.5 border border-slate-100">
            <div className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse"></div>
            <div className="text-xs font-medium text-slate-600">
              {!selectedProgramTypeId && !selectedProgramId && <span>The rules shown below apply to <strong>every program and department</strong> in the entire college.</span>}
              {selectedProgramTypeId && !selectedProgramId && <span>Showing policies restricted specifically to the <strong>{programTypes.find(pt => pt._id === selectedProgramTypeId)?.name}</strong> stream.</span>}
              {selectedProgramId && <span>Showing policies that strictly apply only to <strong>{programs.find(p => p._id === selectedProgramId)?.name}</strong>.</span>}
            </div>
          </div>
        </div>

        {/* Informational Banner */}
        <div className="bg-indigo-900 text-indigo-100 rounded-2xl p-4 shadow-md shadow-indigo-900/10 border border-indigo-950 flex gap-3.5 items-start">
          <AlertCircle className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
          <div className="space-y-0.5">
            <h4 className="text-sm font-bold text-white">How Rules are Processed</h4>
            <p className="text-xs text-indigo-200/90 leading-relaxed">
              When reviewing a student's eligibility, the system checks these rules in order, starting from rule <strong>#1</strong>. 
              The system stops at the very first rule a student qualifies for and applies that decision. Please make sure your rule numbers are arranged in the correct priority order.
            </p>
          </div>
        </div>

        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
          <div className="w-full sm:w-auto space-y-1">
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Filter by Policy Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none font-semibold text-slate-700 focus:border-indigo-500 transition-colors"
            >
              <option value="all">Show All Policies</option>
              <option value="active">Active Policies Only</option>
              <option value="draft">Drafts Only</option>
              <option value="archived">Archived Policies Only</option>
            </select>
          </div>
          <button 
            onClick={fetchPolicies} 
            className="w-full sm:w-auto px-4 py-2 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-2 shadow-sm transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh List
          </button>
        </div>

        {/* Policy Display Section */}
        {policies.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center max-w-lg mx-auto">
            <div className="w-16 h-16 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100">
              <FileText className="w-8 h-8" />
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">No Promotion Policies Found</h3>
            <p className="text-xs text-slate-500 max-w-xs mx-auto mb-6">No rules have been set up for the selected academic department or course yet.</p>
            <button 
              onClick={() => { resetForm(); setShowCreateModal(true); }} 
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Set Up First Policy
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {policies.map((policy) => (
              <div key={policy._id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group overflow-hidden">
                <div className="p-4 sm:p-6 space-y-4">
                  
                  {/* Policy Header Details */}
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pb-4 border-b border-slate-100">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{policy.policyName}</h3>
                        {getStatusBadge(policy.status)}
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-600 rounded border border-slate-200 tracking-wide uppercase">{getScopeLabel(policy)}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed max-w-2xl">{policy.description || 'No additional details provided.'}</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="w-full sm:w-auto flex items-center justify-end gap-1 border-t sm:border-0 pt-3 sm:pt-0">
                      {policy.status === 'draft' && (
                        <button 
                          onClick={() => handleActivatePolicy(policy._id)} 
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all" 
                          title="Activate Policy"
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleEditPolicy(policy)} 
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all" 
                        title="Edit Policy"
                      >
                        <Sliders className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handlePreviewPolicy(policy)} 
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-xl transition-all" 
                        title="Preview Details"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      {policy.status !== 'archived' && (
                        <button 
                          onClick={() => handleArchivePolicy(policy._id)} 
                          className="p-2 text-rose-600 hover:bg-rose-50 rounded-xl transition-all" 
                          title="Archive Policy"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Rules Sequence */}
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-bold tracking-wider text-slate-400 uppercase mb-1">Rules List (Checked in Order)</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {(policy.promotionRules || []).sort((a,b) => a.priority - b.priority).map((rule, idx) => (
                        <div key={idx} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200/60 text-xs">
                          <div className="flex items-start sm:items-center gap-2.5">
                            <span className="inline-flex items-center justify-center font-mono font-bold bg-indigo-50 text-indigo-700 w-6 h-6 rounded-lg border border-indigo-100">
                              #{rule.priority}
                            </span>
                            <div>
                              <div className="font-bold text-slate-800">{rule.ruleName}</div>
                              <div className="text-[11px] text-slate-500 mt-0.5 font-medium">
                                <span className="text-slate-400 font-semibold">Conditions:</span> {formatConditionText(rule.conditions)}
                              </div>
                            </div>
                          </div>
                          <div className="sm:text-right shrink-0">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5 sm:hidden">Action Result</span>
                            {getActionBadge(rule.action)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Policy Form Modal (Create & Edit) */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-fade-in">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden transform transition-all">
            
            {/* Modal Header */}
            <div className="bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center sticky top-0 z-10">
              <div>
                <h2 className="text-base font-black text-slate-900">{showCreateModal ? 'Create New Promotion Policy' : 'Edit Promotion Policy Settings'}</h2>
                <p className="text-xs text-slate-400 mt-0.5">Fill in the basic info and set the rules for promoting students.</p>
              </div>
              <button onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }} className="p-1.5 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 bg-slate-50/50">
              
              <div className="bg-white p-4 rounded-xl border border-slate-200/80 space-y-4 shadow-sm">
                <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider">Basic Policy Information</h3>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-700">Policy Name / Title *</label>
                    <input 
                      type="text" 
                      value={formData.policyName} 
                      onChange={(e) => setFormData({ ...formData, policyName: e.target.value })} 
                      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all" 
                      placeholder="e.g., BCA Passing and Promotion Rules 2026" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-slate-700">Description / Explanatory Note</label>
                    <textarea 
                      value={formData.description} 
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                      className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all" 
                      rows="2" 
                      placeholder="Add any extra notes or explanations about these rules here..." 
                    />
                  </div>
                </div>
              </div>

              {/* Policy Scope Tag */}
              <div className="bg-slate-900 text-slate-100 rounded-xl p-4 border border-slate-950 shadow-inner flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Where this policy applies</div>
                  <div className="text-xs font-bold text-white mt-0.5">
                    {!selectedProgramTypeId && !selectedProgramId && <span>All Courses & Programs in the College</span>}
                    {selectedProgramTypeId && !selectedProgramId && <span>Stream Level: {programTypes.find(pt => pt._id === selectedProgramTypeId)?.name}</span>}
                    {selectedProgramId && <span>Specific Course: {programs.find(p => p._id === selectedProgramId)?.name}</span>}
                  </div>
                </div>
                <div className="text-[10px] font-medium bg-slate-800 text-slate-300 px-2 py-1 rounded border border-slate-700">System Locked</div>
              </div>

              {/* Rules List Section */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Policy Criteria Rules</h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">Add individual rule steps that students will be evaluated against.</p>
                  </div>
                  <button 
                    onClick={() => { setEditingRule(null); resetNewRule(); setShowRuleModal(true); }} 
                    className="text-xs font-bold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 bg-white border border-slate-200 px-2.5 py-1.5 rounded-xl shadow-sm hover:bg-slate-50 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5 stroke-[3]" /> Add New Rule Step
                  </button>
                </div>

                {priorityError && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs font-medium text-rose-700 flex gap-2 items-center">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {priorityError}
                  </div>
                )}

                {formData.promotionRules.length === 0 ? (
                  <div className="text-center bg-white border-2 border-dashed border-slate-200 rounded-xl p-8 text-xs text-slate-400">
                    No rules have been added to this policy yet. Click "Add New Rule Step" above to start.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.promotionRules.sort((a,b) => a.priority - b.priority).map((rule) => (
                      <div key={rule._id} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-colors gap-4">
                        <div className="flex items-start gap-3">
                          <span className="inline-flex items-center justify-center font-mono text-xs font-bold bg-slate-100 text-slate-600 w-5 h-5 rounded border tracking-wide shrink-0 mt-0.5">
                            #{rule.priority}
                          </span>
                          <div>
                            <div className="font-bold text-xs text-slate-900">{rule.ruleName}</div>
                            <div className="text-[11px] font-medium text-slate-500 mt-0.5">
                              {formatConditionText(rule.conditions)} → {getActionBadge(rule.action)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => editRule(rule)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-50 rounded-lg transition-colors" title="Edit Step Details">
                            <Sliders className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteRule(rule._id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-slate-50 rounded-lg transition-colors" title="Delete Step">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer Buttons */}
            <div className="bg-white border-t border-slate-100 px-6 py-4 flex flex-col sm:flex-row justify-end gap-2.5 sticky bottom-0 z-10">
              <button onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }} className="w-full sm:w-auto px-4 py-2.5 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors order-2 sm:order-1">Discard Changes</button>
              <button onClick={showCreateModal ? handleCreatePolicy : handleUpdatePolicy} className="w-full sm:w-auto px-5 py-2.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg shadow-indigo-600/10 transition-colors order-1 sm:order-2">
                {showCreateModal ? 'Create Policy' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Setup Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4 animate-fade-in">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 flex flex-col max-h-[92vh] sm:max-h-[85vh] overflow-hidden">
            
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-sm font-black text-slate-900">{editingRule ? 'Modify Rule Step' : 'Create New Rule Step'}</h2>
                <p className="text-xs text-slate-400 mt-0.5">Specify the exact passing scores and paper limits.</p>
              </div>
              <button onClick={() => { setShowRuleModal(false); setEditingRule(null); resetNewRule(); }} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5 overflow-y-auto flex-1 bg-slate-50/50">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">Rule Name / Short Label *</label>
                <input 
                  type="text" 
                  value={newRule.ruleName} 
                  onChange={(e) => setNewRule({ ...newRule, ruleName: e.target.value })} 
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all" 
                  placeholder="e.g., Direct Pass with Distinction, Maximum 3 Backlogs Allowed" 
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">Rule Check Order (Priority Sequence Number) *</label>
                <input 
                  type="number" 
                  value={newRule.priority} 
                  onChange={(e) => setNewRule({ ...newRule, priority: parseInt(e.target.value) })} 
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all font-mono" 
                  min="1" 
                />
                <p className="text-[10px] font-medium text-slate-400">Rules are checked sequentially starting from #1. Lower numbers are checked first.</p>
              </div>

              <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-4 shadow-sm">
                <div className="text-xs font-bold text-slate-900 pb-2 border-b border-slate-100">Set Rule Criteria Limits</div>
                
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-slate-600">Minimum Required Overall Percentage Score (%)</label>
                  <input 
                    type="number" 
                    value={newRule.conditions.overallPercentage.min} 
                    onChange={(e) => setNewRule({ 
                      ...newRule, 
                      conditions: { 
                        ...newRule.conditions, 
                        overallPercentage: { min: e.target.value } 
                      } 
                    })} 
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-mono" 
                    placeholder="Leave empty if overall score does not matter for this step" 
                    step="0.01" 
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-slate-600">Maximum Allowed Failed Subjects / Papers</label>
                  <input 
                    type="number" 
                    value={newRule.conditions.failedSubjectsCount.max} 
                    onChange={(e) => setNewRule({ 
                      ...newRule, 
                      conditions: { 
                        ...newRule.conditions, 
                        failedSubjectsCount: { max: e.target.value } 
                      } 
                    })} 
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-mono" 
                    placeholder="Leave empty if number of failures does not matter" 
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">Action Result for Students Who Match This Rule *</label>
                <select 
                  value={newRule.action} 
                  onChange={(e) => setNewRule({ ...newRule, action: e.target.value })} 
                  className="w-full px-3 py-2.5 text-xs bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-bold text-slate-700"
                >
                  {actionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="bg-indigo-50/50 p-2.5 rounded-lg border border-indigo-100 text-[10px] text-slate-500 font-medium leading-relaxed">
                  <strong>Outcome explanation:</strong> {actionOptions.find(o => o.value === newRule.action)?.description}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2 bg-white sticky bottom-0 z-10">
              <button onClick={() => { setShowRuleModal(false); setEditingRule(null); resetNewRule(); }} className="px-4 py-2 text-xs font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors">Cancel</button>
              <button onClick={saveRule} className="px-4 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md transition-colors">Add Rule Step</button>
            </div>
          </div>
        </div>
      )}

      {/* Policy Preview Modal */}
      {showPreviewModal && selectedPolicy && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-fade-in">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden">
            
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-sm font-black text-slate-900">Policy Preview</h2>
                <p className="text-xs text-slate-400 mt-0.5">Review a summary of this policy configuration before publishing.</p>
              </div>
              <button onClick={() => { setShowPreviewModal(false); setSelectedPolicy(null); }} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6 overflow-y-auto flex-1 bg-slate-50/50">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-[10px] font-bold text-indigo-500 tracking-wider uppercase">Policy Name</div>
                <h3 className="text-base font-black text-slate-900 mt-0.5">{selectedPolicy.policyName}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{selectedPolicy.description || 'No additional details provided.'}</p>
              </div>
              
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm space-y-1">
                <h4 className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Applicable Area (Scope)</h4>
                <p className="text-xs font-bold text-slate-800">{getScopeLabel(selectedPolicy)}</p>
              </div>
              
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Evaluation Rules Order Overview</h4>
                <div className="space-y-2">
                  {(selectedPolicy.promotionRules || []).sort((a,b) => a.priority - b.priority).map((rule, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-900">{rule.ruleName}</span>
                        <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-500 border rounded px-1.5 py-0.5">Check Order Step #{rule.priority}</span>
                      </div>
                      <div className="py-1">{getActionBadge(rule.action)}</div>
                      
                      <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-xs text-slate-600 space-y-1 mt-1 font-medium">
                        {rule.conditions?.overallPercentage?.min && <div>• Student must achieve a total overall percentage of at least: <strong className="text-slate-900 font-bold">{rule.conditions.overallPercentage.min}%</strong>.</div>}
                        {(rule.conditions?.failedSubjectsCount?.max !== undefined && rule.conditions?.failedSubjectsCount?.max !== null && rule.conditions?.failedSubjectsCount?.max !== '') && 
                          <div>• Total number of backlogs or failed subjects cannot be more than: <strong className="text-slate-900 font-bold">{rule.conditions.failedSubjectsCount.max} subjects</strong>.</div>
                        }
                        {!rule.conditions?.overallPercentage?.min && (!rule.conditions?.failedSubjectsCount?.max || rule.conditions?.failedSubjectsCount?.max === '') && 
                          <div className="text-slate-400 italic">• Fallback rule step (Matches automatically for anyone reaching this point).</div>
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionPolicies;